import React, { useState } from 'react';
import { DataTable } from '../components/DataTable';
import { WizardForm } from '../components/WizardForm';
import { StatusBadge } from '../components/StatusBadge';
import { UserIcon, PhoneIcon, MapPinIcon, BriefcaseIcon, XIcon } from 'lucide-react';
import { beneficiaries } from '../data/beneficiaries';
export function ConservationManagement() {
  const [activeTab, setActiveTab] = useState<'list' | 'add' | 'approval'>('list');
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedBeneficiaries, setSelectedBeneficiaries] = useState<string[]>([]);
  const [beneficiarySearch, setBeneficiarySearch] = useState('');
  const [conservationsList, setConservationsList] = useState([{
    groupType: 'CPG',
    groupName: 'Tiger Monitoring Zone A',
    division: 'Khulna',
    range: 'Satkhira Range',
    station: 'Burigoalini Station',
    formationDate: '2024-01-10',
    status: 'active' as const
  }, {
    groupType: 'DCT',
    groupName: 'Dolphin Protection Drive',
    division: 'Khulna',
    range: 'Khulna Range',
    station: 'Karamjal Station',
    formationDate: '2024-02-15',
    status: 'active' as const
  }, {
    groupType: 'CPG',
    groupName: 'Sundarban Patrol Route B',
    division: 'Bagerhat',
    range: 'Chandpai Range',
    station: 'Harbaria Station',
    formationDate: '2024-01-01',
    status: 'completed' as const
  }, {
    groupType: 'VTRT',
    groupName: 'Eco Tour - Mangrove Trail',
    division: 'Satkhira',
    range: 'Sarankhola Range',
    station: 'Kotka Station',
    formationDate: '2024-03-05',
    status: 'active' as const
  }, {
    groupType: 'VTRT',
    groupName: 'Biodiversity Research 2024',
    division: 'Khulna',
    range: 'Chandpai Range',
    station: 'Joymoni Station',
    formationDate: '2024-04-01',
    status: 'pending' as const
  }, {
    groupType: 'CPG',
    groupName: 'Anti-Poaching Patrol C',
    division: 'Satkhira',
    range: 'Satkhira Range',
    station: 'Kaikhali Station',
    formationDate: '2024-02-20',
    status: 'cancelled' as const
  }]);
  const [formData, setFormData] = useState({
    conservationGroupType: '',
    groupName: '',
    division: '',
    range: '',
    station: '',
    formationDate: '',
    status: 'Active'
  });
  const stationsByRange: Record<string, string[]> = {
    'Satkhira Range': ['Burigoalini Station', 'Kaikhali Station', 'Dobeki Station'],
    'Khulna Range': ['Nalian Station', 'Patkosta Station', 'Karamjal Station'],
    'Chandpai Range': ['Harbaria Station', 'Joymoni Station', 'Tamulbunia Station'],
    'Sarankhola Range': ['Supati Station', 'Sharankhola Station', 'Kotka Station']
  };
  // Filter approved beneficiaries for association
  const approvedBeneficiaries = beneficiaries.filter(b => b.status === 'approved');
  const filteredBeneficiaries = approvedBeneficiaries.filter(b => b.name.toLowerCase().includes(beneficiarySearch.toLowerCase()) || b.nid.includes(beneficiarySearch) || b.profession.toLowerCase().includes(beneficiarySearch.toLowerCase()));
  const pendingApprovals = conservationsList.filter(c => c.status === 'pending');
  const columns = [{
    key: 'groupType',
    label: 'Group Type'
  }, {
    key: 'groupName',
    label: 'Group Name'
  }, {
    key: 'division',
    label: 'Division'
  }, {
    key: 'range',
    label: 'Range'
  }, {
    key: 'station',
    label: 'Station'
  }, {
    key: 'formationDate',
    label: 'Formation Date'
  }, {
    key: 'status',
    label: 'Status',
    render: (value: string) => <StatusBadge status={value as any} data-id="element-516" />
  }];
  const filters = [{
    key: 'groupType',
    label: 'Group Type',
    options: ['CPG', 'VTRT', 'DCT']
  }, {
    key: 'status',
    label: 'Status',
    options: ['Active', 'Completed', 'Cancelled', 'Pending']
  }];
  const wizardSteps = [{
    title: 'Conservation Info',
    description: 'Enter basic conservation details'
  }, {
    title: 'Resource Mapping',
    description: 'Add beneficiary associates to the group'
  }, {
    title: 'Review & Submit',
    description: 'Review all information before submitting'
  }];
  return <div className="space-y-6" data-id="element-517">
      <div data-id="element-518">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-519">
          Conservation Management
        </h1>
        <p className="text-sm text-gray-600 mt-1" data-id="element-520">
          Manage conservation projects, patrols, and eco-tours
        </p>
      </div>

      <div className="border-b border-gray-200" data-id="element-521">
        <div className="flex gap-4" data-id="element-522">
          <button onClick={() => setActiveTab('list')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'list' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-523">
            Conservation List
          </button>
          <button onClick={() => setActiveTab('add')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'add' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-524">
            Add Conservation
          </button>
          <button onClick={() => setActiveTab('approval')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'approval' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-525">
            Approval Queue ({pendingApprovals.length})
          </button>
        </div>
      </div>

      {activeTab === 'list' && <DataTable columns={columns} data={conservationsList} filters={filters} onDataChange={setConservationsList} searchPlaceholder="Search conservations..." data-id="element-526" />}

      {activeTab === 'approval' && <DataTable columns={columns} data={pendingApprovals} onView={row => console.log('View', row)} onApprove={row => {
      setConservationsList(conservationsList.map(c => c.groupName === row.groupName ? {
        ...c,
        status: 'active' as const
      } : c));
      alert(`Approved: ${row.groupName}`);
    }} searchPlaceholder="Search pending approvals..." data-id="element-527" />}

      {activeTab === 'add' && <WizardForm steps={wizardSteps} currentStep={currentStep} onStepChange={setCurrentStep} onSubmit={() => {
      if (!formData.conservationGroupType || !formData.groupName || !formData.division) {
        alert('Please fill in all required fields.');
        return;
      }
      // Add new conservation to the list
      setConservationsList([...conservationsList, {
        groupType: formData.conservationGroupType,
        groupName: formData.groupName,
        division: formData.division,
        range: formData.range,
        station: formData.station,
        formationDate: formData.formationDate,
        status: 'pending' as const
      }]);
      alert(`Conservation Group "${formData.groupName}" submitted for approval!`);
      setCurrentStep(0);
      setActiveTab('approval');
      setSelectedBeneficiaries([]);
      setBeneficiarySearch('');
      setFormData({
        conservationGroupType: '',
        groupName: '',
        division: '',
        range: '',
        station: '',
        formationDate: '',
        status: 'Active'
      });
    }} data-id="element-528">
          {currentStep === 0 && <div className="space-y-4" data-id="element-529">
              <div className="grid grid-cols-2 gap-4" data-id="element-530">
                <div data-id="element-531">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-532">
                    Conservation Group Type *
                  </label>
                  <select required value={formData.conservationGroupType} onChange={e => setFormData({
              ...formData,
              conservationGroupType: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-533">
                    <option value="" data-id="element-534">Select Group Type</option>
                    <option value="CPG" data-id="element-535">CPG (Community Patrol Group)</option>
                    <option value="VTRT" data-id="element-536">
                      VTRT (Village Tiger Response Team)
                    </option>
                    <option value="DCT" data-id="element-537">DCT (Dolphin Conservation Team)</option>
                  </select>
                </div>
                <div data-id="element-538">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-539">
                    Group Name *
                  </label>
                  <input type="text" required maxLength={150} value={formData.groupName} onChange={e => setFormData({
              ...formData,
              groupName: e.target.value
            })} placeholder="Max 150 characters" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-540" />
                  <p className="text-xs text-gray-400 mt-1" data-id="element-541">
                    {formData.groupName.length}/150
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4" data-id="element-542">
                <div data-id="element-543">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-544">
                    Division *
                  </label>
                  <select required value={formData.division} onChange={e => setFormData({
              ...formData,
              division: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-545">
                    <option value="" data-id="element-546">Select Division</option>
                    <option value="Khulna" data-id="element-547">Khulna</option>
                    <option value="Satkhira" data-id="element-548">Satkhira</option>
                    <option value="Bagerhat" data-id="element-549">Bagerhat</option>
                  </select>
                </div>
                <div data-id="element-550">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-551">
                    Range
                  </label>
                  <select value={formData.range} onChange={e => setFormData({
              ...formData,
              range: e.target.value,
              station: ''
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-552">
                    <option value="" data-id="element-553">Select Range</option>
                    <option value="Satkhira Range" data-id="element-554">Satkhira Range</option>
                    <option value="Khulna Range" data-id="element-555">Khulna Range</option>
                    <option value="Chandpai Range" data-id="element-556">Chandpai Range</option>
                    <option value="Sarankhola Range" data-id="element-557">Sarankhola Range</option>
                  </select>
                </div>
                <div data-id="element-558">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-559">
                    Station
                  </label>
                  <select value={formData.station} onChange={e => setFormData({
              ...formData,
              station: e.target.value
            })} disabled={!formData.range} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20] disabled:bg-gray-100 disabled:text-gray-400" data-id="element-560">
                    <option value="" data-id="element-561">
                      {formData.range ? 'Select Station' : 'Select Range first'}
                    </option>
                    {formData.range && stationsByRange[formData.range]?.map(station => <option key={station} value={station} data-id="element-562">
                          {station}
                        </option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-563">
                <div data-id="element-564">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-565">
                    Formation Date
                  </label>
                  <input type="date" value={formData.formationDate} onChange={e => setFormData({
              ...formData,
              formationDate: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-566" />
                </div>
                <div data-id="element-567">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-568">
                    Status
                  </label>
                  <div className="flex items-center gap-4 mt-2" data-id="element-569">
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-570">
                      <input type="radio" name="conservationStatus" value="Active" checked={formData.status === 'Active'} onChange={e => setFormData({
                  ...formData,
                  status: e.target.value
                })} className="w-4 h-4 text-[#1B5E20] focus:ring-[#1B5E20]" data-id="element-571" />
                      <span className="text-sm text-gray-700" data-id="element-572">Active</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-573">
                      <input type="radio" name="conservationStatus" value="Inactive" checked={formData.status === 'Inactive'} onChange={e => setFormData({
                  ...formData,
                  status: e.target.value
                })} className="w-4 h-4 text-[#1B5E20] focus:ring-[#1B5E20]" data-id="element-574" />
                      <span className="text-sm text-gray-700" data-id="element-575">Inactive</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>}

          {currentStep === 1 && <div className="space-y-4" data-id="element-576">
              <div data-id="element-577">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-578">
                  Add Beneficiary Associates
                </label>
                <input type="text" value={beneficiarySearch} onChange={e => setBeneficiarySearch(e.target.value)} placeholder="Search by name, NID, or profession..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20] mb-2" data-id="element-579" />
                <div className="border border-gray-300 rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto" data-id="element-580">
                  {filteredBeneficiaries.map(beneficiary => <label key={beneficiary.nid} className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${selectedBeneficiaries.includes(beneficiary.nid) ? 'bg-green-50 border border-green-200' : 'hover:bg-gray-50 border border-transparent'}`} data-id="element-581">
                      <input type="checkbox" checked={selectedBeneficiaries.includes(beneficiary.nid)} onChange={e => {
                if (e.target.checked) {
                  setSelectedBeneficiaries([...selectedBeneficiaries, beneficiary.nid]);
                } else {
                  setSelectedBeneficiaries(selectedBeneficiaries.filter(id => id !== beneficiary.nid));
                }
              }} className="w-4 h-4 text-[#1B5E20] border-gray-300 rounded focus:ring-[#1B5E20]" data-id="element-582" />
                      <div className="w-8 h-8 bg-[#1B5E20]/10 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-583">
                        <UserIcon className="w-4 h-4 text-[#1B5E20]" data-id="element-584" />
                      </div>
                      <div className="flex-1" data-id="element-585">
                        <span className="text-sm font-medium text-gray-900" data-id="element-586">
                          {beneficiary.name}
                        </span>
                        <span className="text-xs text-gray-500 ml-2" data-id="element-587">
                          {beneficiary.profession} · {beneficiary.village}
                        </span>
                      </div>
                    </label>)}
                  {filteredBeneficiaries.length === 0 && <p className="text-sm text-gray-500 text-center py-2" data-id="element-588">
                      No beneficiaries found
                    </p>}
                </div>
                {selectedBeneficiaries.length > 0 && <p className="text-xs text-[#1B5E20] mt-1 font-medium" data-id="element-589">
                    {selectedBeneficiaries.length} beneficiar
                    {selectedBeneficiaries.length > 1 ? 'ies' : 'y'} selected
                  </p>}
              </div>

              {/* Selected Beneficiaries Information */}
              {selectedBeneficiaries.length > 0 && <div className="space-y-3" data-id="element-590">
                  <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-591">
                    <UserIcon className="w-4 h-4 text-[#1B5E20]" data-id="element-592" />
                    Selected Beneficiary Associates
                  </h4>
                  {selectedBeneficiaries.map(nid => {
            const beneficiary = approvedBeneficiaries.find(b => b.nid === nid);
            if (!beneficiary) return null;
            return <div key={nid} className="bg-green-50 border border-green-200 rounded-lg p-4" data-id="element-593">
                        <div className="flex items-start gap-4" data-id="element-594">
                          <div className="w-12 h-12 bg-[#1B5E20]/10 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-595">
                            <UserIcon className="w-6 h-6 text-[#1B5E20]" data-id="element-596" />
                          </div>
                          <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm flex-1" data-id="element-597">
                            <div data-id="element-598">
                              <span className="text-gray-500" data-id="element-599">Name:</span>{' '}
                              <span className="font-medium text-gray-900" data-id="element-600">
                                {beneficiary.name}
                              </span>
                            </div>
                            <div data-id="element-601">
                              <span className="text-gray-500" data-id="element-602">NID:</span>{' '}
                              <span className="font-medium text-gray-900" data-id="element-603">
                                {beneficiary.nid}
                              </span>
                            </div>
                            <div data-id="element-604">
                              <span className="text-gray-500" data-id="element-605">Phone:</span>{' '}
                              <span className="font-medium text-gray-900" data-id="element-606">
                                {beneficiary.phone}
                              </span>
                            </div>
                            <div data-id="element-607">
                              <span className="text-gray-500" data-id="element-608">Village:</span>{' '}
                              <span className="font-medium text-gray-900" data-id="element-609">
                                {beneficiary.village}
                              </span>
                            </div>
                            <div data-id="element-610">
                              <span className="text-gray-500" data-id="element-611">Profession:</span>{' '}
                              <span className="font-medium text-gray-900" data-id="element-612">
                                {beneficiary.profession}
                              </span>
                            </div>
                            <div data-id="element-613">
                              <span className="text-gray-500" data-id="element-614">Email:</span>{' '}
                              <span className="font-medium text-gray-900" data-id="element-615">
                                {beneficiary.email}
                              </span>
                            </div>
                          </div>
                          <button type="button" onClick={() => setSelectedBeneficiaries(selectedBeneficiaries.filter(id => id !== nid))} className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0" title="Remove beneficiary" data-id="element-616">
                            <XIcon className="w-4 h-4" data-id="element-617" />
                          </button>
                        </div>
                      </div>;
          })}
                </div>}
            </div>}

          {currentStep === 2 && <div className="space-y-6" data-id="element-618">
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-619">
                <div className="flex items-center justify-between mb-3" data-id="element-620">
                  <h4 className="font-semibold text-gray-900" data-id="element-621">
                    Conservation Info
                  </h4>
                  <button onClick={() => setCurrentStep(0)} className="text-sm text-[#1B5E20] hover:underline" data-id="element-622">
                    Edit
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-623">
                  <div data-id="element-624">
                    <span className="text-gray-600" data-id="element-625">Group Type:</span>{' '}
                    <span className="font-medium" data-id="element-626">
                      {formData.conservationGroupType || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-627">
                    <span className="text-gray-600" data-id="element-628">Group Name:</span>{' '}
                    <span className="font-medium" data-id="element-629">
                      {formData.groupName || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-630">
                    <span className="text-gray-600" data-id="element-631">Division:</span>{' '}
                    <span className="font-medium" data-id="element-632">
                      {formData.division || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-633">
                    <span className="text-gray-600" data-id="element-634">Range:</span>{' '}
                    <span className="font-medium" data-id="element-635">
                      {formData.range || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-636">
                    <span className="text-gray-600" data-id="element-637">Station:</span>{' '}
                    <span className="font-medium" data-id="element-638">
                      {formData.station || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-639">
                    <span className="text-gray-600" data-id="element-640">Formation Date:</span>{' '}
                    <span className="font-medium" data-id="element-641">
                      {formData.formationDate || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-642">
                    <span className="text-gray-600" data-id="element-643">Status:</span>{' '}
                    <span className={`font-medium ${formData.status === 'Active' ? 'text-green-700' : 'text-gray-500'}`} data-id="element-644">
                      {formData.status}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4" data-id="element-645">
                <div className="flex items-center justify-between mb-3" data-id="element-646">
                  <h4 className="font-semibold text-gray-900" data-id="element-647">
                    Beneficiary Associates
                  </h4>
                  <button onClick={() => setCurrentStep(1)} className="text-sm text-[#1B5E20] hover:underline" data-id="element-648">
                    Edit
                  </button>
                </div>
                <div className="text-sm" data-id="element-649">
                  <span className="text-gray-600" data-id="element-650">Associates:</span>{' '}
                  <span className="font-medium" data-id="element-651">
                    {selectedBeneficiaries.length > 0 ? selectedBeneficiaries.map(nid => {
                const b = approvedBeneficiaries.find(ben => ben.nid === nid);
                return b ? b.name : nid;
              }).join(', ') : 'Not provided'}
                  </span>
                </div>
              </div>
            </div>}
        </WizardForm>}
    </div>;
}